/**
 * MarketIntelCompetitorsPage — /omega/market-intel/competitors
 *
 * Latest competitor scan viewer + scan trigger for the Market Intelligence Brain
 * (Phase 40 V-025). Fetches /api/v1/market-intel/competitors?category={category}.
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

const CATEGORIES = [
  'Financial',
  'HealthAndWellbeing',
  'TechnologyAndInfrastructure',
  'WorkAndProductivity',
];

interface CompetitorScan {
  scan_id: string;
  category: string;
  competitors_json: string;
  top_competitor: string | null;
  scan_sources: string;
  scanned_at: number;
}

interface CompetitorsResponse {
  scan: CompetitorScan | null;
}

export function MarketIntelCompetitorsPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [category, setCategory] = useState('Financial');
  const [scanning, setScanning] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['market-intel-competitors', category],
    queryFn: () => apiFetch<CompetitorsResponse>(`/market-intel/competitors?category=${category}`),
  });

  const scan = data?.scan;

  async function triggerScan() {
    setScanning(true);
    try {
      await apiFetch('/market-intel/competitors/scan', { method: 'POST', body: JSON.stringify({ category }) });
      refetch();
    } finally {
      setScanning(false);
    }
  }

  return (
    <div className="omega-page" style={{ padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Market Intel', to: '/omega/market-intel' },
          { label: 'Competitors' },
        ]}
      />

      <h1 style={{ fontSize: '12pt', fontWeight: 700, margin: '12px 0 16px' }}>
        Competitor Intelligence
      </h1>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <div>
          <label style={{ fontSize: '9pt', marginRight: 8 }}>Category:</label>
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            style={{ fontSize: '9pt', padding: '4px 8px' }}
          >
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button
          onClick={triggerScan}
          disabled={scanning}
          style={{ fontSize: '8.5pt', padding: '5px 12px', cursor: scanning ? 'not-allowed' : 'pointer' }}
        >
          {scanning ? 'Scanning…' : 'Trigger Scan'}
        </button>
      </div>

      {isLoading && <p style={{ fontSize: '9pt' }}>Loading...</p>}
      {error && <p style={{ fontSize: '9pt', color: '#f87171' }}>Error loading scan.</p>}
      {!isLoading && !scan && (
        <p style={{ fontSize: '9pt', color: 'var(--omega-ink-muted)' }}>No scan yet for {category}.</p>
      )}

      {scan && (
        <div style={{ fontSize: '8.5pt' }}>
          <p><strong>Top competitor:</strong> {scan.top_competitor ?? '—'}</p>
          <p><strong>Sources:</strong> {scan.scan_sources}</p>
          <p><strong>Scanned:</strong> {new Date(scan.scanned_at * 1000).toLocaleString()}</p>
          <pre style={{ marginTop: 12, overflowX: 'auto', whiteSpace: 'pre-wrap', fontSize: '8pt' }}>
            {scan.competitors_json}
          </pre>
        </div>
      )}
    </div>
  );
}
